import React, { useState, useEffect, useCallback, useMemo } from 'react';
import jsPDF from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import {
  Calculator, Copy, Download, Info, Loader2, TrendingUp, TrendingDown,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

/**
 * PayoutEstimator — live Gross→Net breakdown for a hypothetical order.
 * Used on the Vendor Payouts page and the Admin Payouts console.
 *
 * Props:
 *   - vendorId?: string     (admin-only override; vendors always target self)
 *   - compact?: boolean     (hide header/description — for embedding in drawers)
 *   - defaults?: object     ({ product_price, zone, weight_grams, speed })
 */
const ZONES = [
  { value: 'A', label: 'A — Local/Intra-city' },
  { value: 'B', label: 'B — Metro↔Metro' },
  { value: 'C', label: 'C — Regional' },
  { value: 'D', label: 'D — Rest of India' },
  { value: 'E', label: 'E — Remote / NE / J&K' },
];

const SPEEDS = [
  { value: 'surface', label: 'Surface' },
  { value: 'air', label: 'Air' },
];

const fmtINR = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 2,
  }).format(Number(n || 0));

/** Human-readable commission source label */
const _commissionSourceLabel = (r) => {
  switch (r?.commission_rate_source) {
    case 'override':     return 'custom override';
    case 'subscription': return 'subscription plan';
    case 'plan':         return 'subscription plan';
    case 'default':      return 'fallback — no active subscription';
    default:             return r?.commission_rate_source || 'unknown';
  }
};

const PayoutEstimator = ({ vendorId, compact = false, defaults = {} }) => {
  const { toast } = useToast();

  const [form, setForm] = useState({
    product_price: defaults.product_price ?? 155,
    zone: defaults.zone ?? 'D',
    weight_grams: defaults.weight_grams ?? 500,
    actual_carrier_bill: defaults.actual_carrier_bill ?? 0,
    is_return: false,
    speed: defaults.speed ?? 'surface',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounced estimator call
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!form.product_price || form.product_price <= 0) {
        setResult(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const body = { ...form };
        if (vendorId) body.vendor_id = vendorId;
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payouts/estimate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
          },
        );
        if (!resp.ok) {
          const detail = await resp.text();
          setError(`HTTP ${resp.status}: ${detail.slice(0, 200)}`);
          setResult(null);
        } else {
          const data = await resp.json();
          setResult(data);
        }
      } catch (e) {
        setError(e?.message || 'Failed to estimate');
        setResult(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [form, vendorId]);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Build 10-line deduction table from the breakdown
  const lines = useMemo(() => {
    if (!result) return [];
    return [
      { k: 'TDS (0.1%)',              v: result.tds,              group: 'tax' },
      { k: 'TCS (0.5%)',              v: result.tcs,              group: 'tax' },
      { k: `Shipping Base (Zone ${result.inputs?.zone})`, v: result.shipping_base, group: 'logistics' },
      { k: 'Shipping GST (18%)',      v: result.shipping_gst,     group: 'logistics' },
      { k: 'Seller Pool (5%, Zone-E capped)', v: result.seller_pool,      group: 'logistics',
        hint: result.seller_pool_raw !== result.seller_pool
          ? `Raw ${fmtINR(result.seller_pool_raw)} capped at ${fmtINR(result.seller_pool_cap)}`
          : null },
      { k: `Commission (${((result.commission_rate || 0) * 100).toFixed(1)}%, ${_commissionSourceLabel(result)})`, v: result.commission, group: 'platform' },
      { k: 'Commission GST (18%)',    v: result.commission_gst,   group: 'platform' },
      { k: 'PG Fee (2%)',             v: result.pg_fee,           group: 'platform' },
      { k: 'PG GST (18%)',            v: result.pg_gst,           group: 'platform' },
      { k: 'Payout Fee (0.25%)',      v: result.payout_fee,       group: 'platform' },
    ];
  }, [result]);

  // Waterfall consumption chips (only when carrier bill is set)
  const waterfall = useMemo(() => {
    if (!result || !result.actual_carrier_bill) return null;
    return {
      seller_consumed: result.seller_pool_consumed || 0,
      marketplace_consumed: result.marketplace_pool_consumed || 0,
      surcharge: result.surcharge || 0,
    };
  }, [result]);

  const handleCopyCSV = useCallback(() => {
    if (!result) return;
    const rows = [
      ['Component', 'Amount (INR)'],
      ['Product Price', result.product_price],
      ['Shipping Base', result.shipping_base],
      ['Shipping GST', result.shipping_gst],
      ['Surcharge', result.surcharge],
      ['Gross Total', result.gross_total],
      ...lines.map((l) => [l.k, l.v]),
      ['Logistics Deduction', result.logistics_deduction],
      ['Total Deductions', result.total_deductions],
      ['NET PAYOUT', result.net_payout],
    ];
    const csv = rows
      .map((r) => r.map((c) => (typeof c === 'string' ? `"${c}"` : c)).join(','))
      .join('\n');
    navigator.clipboard.writeText(csv).then(() => {
      toast({ title: 'Copied', description: 'Breakdown copied as CSV' });
    });
  }, [result, lines, toast]);

  const handleDownloadPDF = useCallback(() => {
    if (!result) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 48;
    let y = margin;

    doc.setFontSize(18);
    doc.text('Aniyor — Payout Estimate Statement', margin, y);
    y += 24;

    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
    y += 20;

    doc.setTextColor(0);
    doc.setFontSize(11);
    const inp = result.inputs || {};
    doc.text(
      `Inputs: Product ₹${inp.product_price} · Zone ${inp.zone} · ` +
      `${inp.weight_grams}g · ${inp.speed} · ` +
      `Carrier Bill ₹${inp.actual_carrier_bill} · ` +
      `${inp.is_return ? 'RETURN' : 'Forward'}`,
      margin, y,
    );
    y += 24;

    // Gross
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text(`Gross Total: ₹${result.gross_total?.toFixed(2)}`, margin, y);
    y += 24;

    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    doc.text('Deductions:', margin, y);
    y += 16;

    lines.forEach((l) => {
      doc.text(l.k, margin + 12, y);
      doc.text(`₹${Number(l.v || 0).toFixed(2)}`, 480, y, { align: 'right' });
      y += 16;
    });

    y += 6;
    doc.setDrawColor(180);
    doc.line(margin, y, 520, y);
    y += 16;

    doc.setFont(undefined, 'bold');
    doc.text('Total Deductions', margin + 12, y);
    doc.text(`₹${result.total_deductions?.toFixed(2)}`, 480, y, { align: 'right' });
    y += 20;

    doc.setFontSize(14);
    doc.setTextColor(16, 122, 60);
    doc.text('NET PAYOUT', margin + 12, y);
    doc.text(`₹${result.net_payout?.toFixed(2)}`, 480, y, { align: 'right' });

    y += 32;
    doc.setTextColor(150);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(
      'Marketplace Pool (3% of P) is a platform allocation used for the',
      margin, y,
    );
    y += 12;
    doc.text(
      'surcharge threshold and is NOT deducted from the vendor payout.',
      margin, y,
    );

    doc.save(`aniyor-payout-estimate-${Date.now()}.pdf`);
    toast({ title: 'Downloaded', description: 'PDF statement saved' });
  }, [result, lines, toast]);

  const groupColor = {
    tax:        'text-amber-700 bg-amber-50',
    platform:   'text-indigo-700 bg-indigo-50',
    logistics:  'text-sky-700 bg-sky-50',
  };

  return (
    <Card data-testid="payout-estimator-card">
      {!compact && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" /> Payout Estimator
          </CardTitle>
          <CardDescription>
            Live Gross → Net breakdown. Plug in a hypothetical product price,
            zone, weight and optional actual carrier bill to see the exact
            deduction ladder.
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className="space-y-6">
        {/* ── Inputs ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="pe-price">Product Price (₹)</Label>
            <Input
              id="pe-price"
              data-testid="pe-product-price-input"
              type="number" min="0" step="1"
              value={form.product_price}
              onChange={(e) => update('product_price')(Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label htmlFor="pe-zone">Zone</Label>
            <Select value={form.zone} onValueChange={update('zone')}>
              <SelectTrigger data-testid="pe-zone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZONES.map((z) => (
                  <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="pe-weight">Weight (grams)</Label>
            <Input
              id="pe-weight"
              data-testid="pe-weight-input"
              type="number" min="1" step="50"
              value={form.weight_grams}
              onChange={(e) => update('weight_grams')(Number(e.target.value) || 500)}
            />
          </div>
          <div>
            <Label htmlFor="pe-speed">Speed</Label>
            <Select value={form.speed} onValueChange={update('speed')}>
              <SelectTrigger data-testid="pe-speed-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPEEDS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="pe-bill">Actual Carrier Bill (₹, optional)</Label>
            <Input
              id="pe-bill"
              data-testid="pe-carrier-bill-input"
              type="number" min="0" step="1"
              value={form.actual_carrier_bill}
              onChange={(e) => update('actual_carrier_bill')(Number(e.target.value) || 0)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave 0 until Delhivery invoice reconciles.
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="pe-return"
                data-testid="pe-is-return-switch"
                checked={form.is_return}
                onCheckedChange={update('is_return')}
              />
              <Label htmlFor="pe-return" className="cursor-pointer">
                Treat as Return
              </Label>
            </div>
          </div>
        </div>

        {/* ── Status strip ──────────────────────────────── */}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
          </div>
        )}
        {error && (
          <div className="text-sm text-red-600" data-testid="pe-error">{error}</div>
        )}

        {/* ── Result ────────────────────────────────────── */}
        {result && !loading && (
          <div className="space-y-5" data-testid="pe-result">
            {/* Gross tile */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Gross Total</p>
                  <p className="text-2xl font-semibold" data-testid="pe-gross">
                    {fmtINR(result.gross_total)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    P + S + S_GST + Surcharge
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-rose-50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Total Deductions</p>
                  <p className="text-2xl font-semibold text-rose-700"
                     data-testid="pe-total-deductions">
                    {fmtINR(result.total_deductions)}
                  </p>
                </CardContent>
              </Card>
              <Card className={result.is_return ? 'bg-red-50' : 'bg-emerald-50'}>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">
                    {result.is_return ? 'Vendor Owes' : 'NET PAYOUT'}
                  </p>
                  <p className={`text-2xl font-semibold ${
                    result.is_return ? 'text-red-700' : 'text-emerald-700'
                  }`} data-testid="pe-net-payout">
                    {result.is_return ? '-' : ''}{fmtINR(Math.abs(result.net_payout))}
                  </p>
                  {!result.is_return && result.gross_total > 0 && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Margin {((result.net_payout / result.gross_total) * 100).toFixed(1)}%
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Fallback-commission warning — plan-less vendors */}
            {result.commission_rate_source === 'default' && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Commission using fallback rate.</strong> This vendor has
                  no active subscription and no custom override — the platform's
                  default 10% is being applied. Assign a subscription plan to
                  the vendor (Admin → Vendors → Subscription) to move to a
                  plan-driven rate.
                </span>
              </div>
            )}

            {/* Waterfall consumption (when B > 0) */}
            {waterfall && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3">
                <p className="text-xs font-medium text-sky-900 mb-2 flex items-center gap-1">
                  <Info className="h-3 w-3" /> Waterfall consumption (5% → 3% → surcharge)
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline" className="bg-white">
                    Seller Pool used: <b className="ml-1">{fmtINR(waterfall.seller_consumed)}</b>
                  </Badge>
                  <Badge variant="outline" className="bg-white">
                    Marketplace Pool used: <b className="ml-1">{fmtINR(waterfall.marketplace_consumed)}</b>
                  </Badge>
                  {waterfall.surcharge > 0 ? (
                    <Badge variant="destructive">
                      Surcharge: {fmtINR(waterfall.surcharge)}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800">
                      No surcharge — within pool budget
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* 10-line breakdown */}
            <div className="rounded-lg border">
              <div className="px-4 py-3 border-b bg-slate-50">
                <h4 className="text-sm font-medium">Deduction Ladder</h4>
              </div>
              <div className="divide-y">
                {lines.map((l, i) => (
                  <div key={i}
                       className="px-4 py-2 flex items-center justify-between"
                       data-testid={`pe-line-${i}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${groupColor[l.group]}`}>
                        {l.group}
                      </span>
                      <span>{l.k}</span>
                      {l.hint && (
                        <span className="text-xs text-muted-foreground italic">
                          ({l.hint})
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-medium tabular-nums">
                      {fmtINR(l.v)}
                    </span>
                  </div>
                ))}
                <div className="px-4 py-2 flex items-center justify-between bg-slate-50 text-sm font-semibold">
                  <span>Logistics Deduction (S + S_GST + Seller Pool + Surcharge)</span>
                  <span className="tabular-nums">{fmtINR(result.logistics_deduction)}</span>
                </div>
              </div>
            </div>

            {/* Info banner */}
            <div className="text-xs text-muted-foreground flex items-start gap-2 px-1">
              <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                Marketplace Pool (3% of Product · {fmtINR(result.marketplace_pool)})
                is a platform allocation used as the surcharge threshold and is
                NOT deducted from your payout. The 5% Seller Pool is capped at
                the Max-Zone-E rate ({fmtINR(result.seller_pool_cap)}) to protect
                high-value items.
              </span>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button
                variant="outline" size="sm" onClick={handleCopyCSV}
                data-testid="pe-copy-csv-btn"
              >
                <Copy className="h-4 w-4 mr-1" /> Copy as CSV
              </Button>
              <Button
                variant="outline" size="sm" onClick={handleDownloadPDF}
                data-testid="pe-download-pdf-btn"
              >
                <Download className="h-4 w-4 mr-1" /> Download PDF Statement
              </Button>
              {result.is_return && (
                <Badge variant="destructive" className="ml-auto">
                  <TrendingDown className="h-3 w-3 mr-1" /> Return mode
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PayoutEstimator;
